import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Col, Form, message, Modal, Radio, Row, Skeleton } from 'antd';

import FormInput from 'components/FormInput/FormInput';
import FormSelection from 'components/FormSelection/FormSelection';
import SectionCard from 'components/SectionCard/SectionCard';
import SectionContainer from 'components/SectionContainer/SectionContainer';
import Title from 'components/Title/Title';

const { useForm, Item: FormItem } = Form;
const { confirm } = Modal;
const { Group: RadioGroup, Button: RadioButton } = Radio;

const AddExpensesModal = ({ isLoading, visible, onClose, onOk, onRemove, defaultValues = {}, loanTypes, fixedPaymentTypes, variablePaymentTypes }) => {
  const [form] = useForm();
  const [selectedCategory, setSelectedCategory] = useState('loanPayments');
  const [selectedType, setSelectedType] = useState('');
  const isOtherPayments = selectedType === 'otherFixedPayment' || selectedType === 'otherVariablePayment';

  useEffect(() => {
    if (defaultValues.category) {
      setSelectedCategory(defaultValues.category);
    }
    if (defaultValues.type) {
      setSelectedType(defaultValues.type);
    }
    if (!defaultValues) {
      form.setFieldsValue({
        type: undefined
      });
    }
  }, [defaultValues, form, selectedCategory]);

  const handleOnOkButtonClick = async e => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const newValues = {
        ...values,
        category: selectedCategory
      };
      onOk(newValues);
    } catch (error) {
      error.errorFields.forEach(field => message.error(field.errors[0]));
    }
  };

  const handleOnRemoveButtonClick = () => {
    confirm({
      title: 'Do you want to remove this?',
      okText: 'Yes',
      onOk() {
        onRemove();
      },
      onCancel() {}
    });
  };

  const footer = defaultValues
    ? [
        <Button key="remove" onClick={handleOnRemoveButtonClick}>
          Remove
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          Save this payment
        </Button>
      ]
    : [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          Add this payment
        </Button>
      ];

  return (
    <Modal title="Cash Outflow Payment Details" visible={visible} onOk={handleOnOkButtonClick} onCancel={onClose} footer={footer} width="70%">
      <Form form={form} initialValues={defaultValues}>
        {isLoading ? (
          <Skeleton active />
        ) : (
          <>
            <SectionContainer>
              <Title marginBottom="8px" type="question">
                Choose one category of expenses
              </Title>
              <RadioGroup buttonStyle="solid" defaultValue={selectedCategory} disabled={!!defaultValues.category} onChange={e => setSelectedCategory(e.target.value)}>
                <RadioButton value="loanPayments">Loan Payment</RadioButton>
                <RadioButton value="variablePayments">Variable Payment</RadioButton>
                <RadioButton value="fixedPayments">Fixed Payment</RadioButton>
              </RadioGroup>
            </SectionContainer>
            {selectedCategory === 'loanPayments' && (
              <SectionCard title="Loan Payment">
                <Row gutter={16}>
                  <Col span={24}>
                    <FormSelection label="Type of Loan" name="type" placeholder="Select loan type" requiredErrorMessage="Please select type of loan." selections={loanTypes} />
                  </Col>
                  <Col span={24}>
                    <FormInput label="Loan Balance" name="loanBalance" placeholder="0.00" type="financial" requiredErrorMessage="Please enter current loan's balance." />
                  </Col>
                  <Col span={24}>
                    <FormItem name="isInsuranceCovered" valuePropName="checked">
                      <Checkbox>Cover with MRTA/MLTA</Checkbox>
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <SectionCard title="Self">
                      <FormInput
                        label="Monthly Payment"
                        name={['amount', 'self']}
                        placeholder="0.00"
                        type="financial"
                        requiredErrorMessage="Please enter monthly payment of this loan."
                      />
                    </SectionCard>
                  </Col>
                  <Col span={12}>
                    <SectionCard title="Spouse">
                      <FormInput label="Monthly Payment" name={['amount', 'spouse']} placeholder="0.00" type="financial" />
                    </SectionCard>
                  </Col>
                </Row>
              </SectionCard>
            )}

            {selectedCategory === 'variablePayments' && (
              <SectionCard title="Variable Payment">
                <Row gutter={16}>
                  <Col span={24}>
                    <FormSelection
                      label="Payment Type"
                      name="type"
                      placeholder="Select a payment type"
                      requiredErrorMessage="Please select a payment type."
                      selections={variablePaymentTypes}
                      extraProps={{
                        onChange: value => setSelectedType(value)
                      }}
                    />
                  </Col>
                  {isOtherPayments && (
                    <Col span={24}>
                      <FormInput label="Label" name="label" placeholder="Enter a label for this payment" requiredErrorMessage="Please enter label for this payment." />
                    </Col>
                  )}
                  <Col span={12}>
                    <SectionCard title="Self">
                      <FormInput label="Amount" name={['amount', 'self']} placeholder="0.00" type="financial" requiredErrorMessage={`Please enter amount of this type.`} />
                    </SectionCard>
                  </Col>
                  <Col span={12}>
                    <SectionCard title="Spouse">
                      <FormInput label="Amount" name={['amount', 'spouse']} placeholder="0.00" type="financial" />
                    </SectionCard>
                  </Col>
                </Row>
              </SectionCard>
            )}

            {selectedCategory === 'fixedPayments' && (
              <SectionCard title="Fixed Payment">
                <Row gutter={16}>
                  <Col span={24}>
                    <FormSelection
                      label="Payment Type"
                      name="type"
                      placeholder="Select a payment type"
                      requiredErrorMessage="Please select a payment type."
                      selections={fixedPaymentTypes}
                      extraProps={{
                        onChange: value => setSelectedType(value)
                      }}
                    />
                  </Col>
                  {isOtherPayments && (
                    <Col span={24}>
                      <FormInput label="Label" name="label" placeholder="Enter a label for this payment" requiredErrorMessage="Please enter label for this payment." />
                    </Col>
                  )}
                  <Col span={12}>
                    <SectionCard title="Self">
                      <FormInput label="Amount" name={['amount', 'self']} placeholder="0.00" type="financial" requiredErrorMessage="Please enter amount of this type." />
                    </SectionCard>
                  </Col>
                  <Col span={12}>
                    <SectionCard title="Spouse">
                      <FormInput label="Amount" name={['amount', 'spouse']} placeholder="0.00" type="financial" />
                    </SectionCard>
                  </Col>
                </Row>
              </SectionCard>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddExpensesModal;
